import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * ScrollManager
 * -------------
 * Global scroll-position controller mounted once inside <BrowserRouter>.
 *
 *  - PUSH / REPLACE navigation → scroll to the top of the new page.
 *  - POP navigation (browser Back / Forward) → restore the exact scroll
 *    position the user had on that history entry previously.
 *
 * Why the restore is more involved than a single scrollTo
 * --------------------------------------------------------
 * 1. The incoming page loads its content asynchronously (category fetch,
 *    products fetch, FAQs). We must wait until the page is tall enough to
 *    support the target position before scrolling.
 * 2. While the restore is in flight, programmatic scrolls and browser
 *    clamps fire scroll events. If those were recorded, they would corrupt
 *    the saved position. A `restoring` flag suppresses recording.
 * 3. Chromium's scroll anchoring will "helpfully" drag the restored
 *    position downward as late content (images, FAQ sections) streams in
 *    above/below the anchor node. We disable `overflow-anchor` on <html>
 *    during the restore + a short settle window, and keep the position
 *    pinned to the target during that window.
 * 4. If the user starts scrolling themselves (wheel/touch), we abort
 *    immediately so we never fight the user.
 *
 * Pages with their own async pagination (e.g. CategoryPage infinite
 * scroll) persist how much content they had loaded via
 * saveLocationState()/readLocationState() keyed by location.key, and
 * re-fetch that amount on POP so the page reaches full height in one
 * commit — no scroll-to-bottom nudging needed.
 */

const STORE_KEY = "vvs_scroll_positions_v1";
const AUX_KEY = "vvs_location_state_v1";
const RESTORE_TIMEOUT_MS = 6000;
const SETTLE_MS = 1200;

let currentKey = null;
let listenerBound = false;
let restoreGen = 0;
let restoring = false;

const readJson = (storageKey) => {
  try {
    return JSON.parse(sessionStorage.getItem(storageKey) || "{}");
  } catch {
    return {};
  }
};

const writeJson = (storageKey, map) => {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    /* quota / privacy-mode – silently ignore */
  }
};

const readPositions = () => readJson(STORE_KEY);

const writePosition = (key, y) => {
  if (!key) return;
  const map = readPositions();
  map[key] = y;
  writeJson(STORE_KEY, map);
};

/** Persist arbitrary per-history-entry UI state (e.g. loaded item count). */
export const saveLocationState = (key, data) => {
  if (!key) return;
  const map = readJson(AUX_KEY);
  map[key] = { ...(map[key] || {}), ...data };
  writeJson(AUX_KEY, map);
};

/** Read back per-history-entry UI state saved via saveLocationState. */
export const readLocationState = (key) => (key ? readJson(AUX_KEY)[key] : undefined);

const bindGlobalScrollListener = () => {
  if (listenerBound || typeof window === "undefined") return;
  listenerBound = true;
  window.addEventListener(
    "scroll",
    () => {
      if (!restoring && currentKey) writePosition(currentKey, window.scrollY);
    },
    { passive: true },
  );
};

const disableNativeRestoration = () => {
  if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
};

const restoreScrollWhenReady = (targetY) => {
  const gen = ++restoreGen;
  restoring = true;
  const html = document.documentElement;
  const prevAnchor = html.style.overflowAnchor;
  // Stop browser scroll anchoring from dragging the restored position
  // while late content streams in.
  html.style.overflowAnchor = "none";
  const start = Date.now();
  let userTookOver = false;
  const onUserInput = () => {
    userTookOver = true;
  };
  window.addEventListener("wheel", onUserInput, { passive: true });
  window.addEventListener("touchstart", onUserInput, { passive: true });

  const cleanup = () => {
    window.removeEventListener("wheel", onUserInput);
    window.removeEventListener("touchstart", onUserInput);
    html.style.overflowAnchor = prevAnchor;
  };

  const finish = () => {
    cleanup();
    if (gen === restoreGen) {
      restoring = false;
      writePosition(currentKey, window.scrollY);
    }
  };

  // Phase 2: position reached — keep it pinned while late content
  // (FAQ section, images) commits, then hand control back to the user.
  const settle = (until) => {
    if (gen !== restoreGen) {
      cleanup();
      return;
    }
    if (userTookOver || Date.now() > until) {
      finish();
      return;
    }
    const maxScroll = html.scrollHeight - window.innerHeight;
    const want = Math.min(targetY, Math.max(maxScroll, 0));
    if (Math.abs(window.scrollY - want) > 1) window.scrollTo(0, want);
    requestAnimationFrame(() => settle(until));
  };

  // Phase 1: wait (without scrolling) until the page is tall enough.
  const step = () => {
    if (gen !== restoreGen) {
      cleanup();
      return;
    }
    if (userTookOver) {
      cleanup();
      if (gen === restoreGen) restoring = false;
      return;
    }
    const maxScroll = html.scrollHeight - window.innerHeight;
    if (maxScroll >= targetY || Date.now() - start >= RESTORE_TIMEOUT_MS) {
      window.scrollTo(0, Math.min(targetY, Math.max(maxScroll, 0)));
      settle(Date.now() + SETTLE_MS);
      return;
    }
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

export const ScrollManager = () => {
  const { pathname, key } = useLocation();
  const navType = useNavigationType();

  // Update the module-level pointer DURING render (synchronously). This
  // must happen before commit so the shared scroll listener writes to the
  // NEW key when the browser clamps scrollY after DOM mutations for the
  // shorter incoming page — leaving the OLD key's stored value untouched.
  currentKey = key;

  useEffect(() => {
    disableNativeRestoration();
    bindGlobalScrollListener();
    // Persist the initial position under the very first key so a
    // reload-then-back scenario has a value to restore to.
    if (!restoring) writePosition(key, window.scrollY);
  }, []);

  useEffect(() => {
    if (navType === "POP") {
      const saved = readPositions()[key];
      if (typeof saved === "number" && saved > 0) {
        restoreScrollWhenReady(saved);
        return;
      }
    }
    // New forward navigation (or nothing to restore): cancel any in-flight
    // restore and go to the top.
    restoreGen += 1;
    restoring = false;
    window.scrollTo(0, 0);
  }, [pathname, key, navType]);

  return null;
};

export default ScrollManager;
